import { css, styled } from "../../../../../../styles";

export const Container = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "40px",
  padding: "46px 32px",

  height: "100%",
  maxHeight: "856px",
  width: "100%",

  overflowY: "auto",
  scrollbarWidth: "none",

  position: "relative",
});

export const MessageContainer = styled("div", {
  display: "flex",
  flexDirection: "column",

  maxWidth: "500px",
  minWidth: "300px",
  width: "max-content",

  alignSelf: "flex-end",

  backgroundColor: "$gray-30",
  borderRadius: "5px",
  padding: "12px 18px 8px 16px",

  variants: {
    isPatientMessage: {
      true: {
        maxWidth: "400px",

        alignSelf: "flex-start",

        boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.16)",
        backgroundColor: "$white",
      },
    },
  },
});

export const MessageTitle = styled("p", {
  color: "$black",
  fontSize: "14px",
  fontWeight: "600",
  lineHeight: "24px",
  verticalAlign: "middle",
  marginBottom: "16px",
});

export const MessageLine = styled("p", {
  color: "$charcoal",
  fontWeight: "300",
  marginTop: "16px",
  lineHeight: "24px",
  letterSpacing: "0px",
});

export const Timestamp = styled("div", {
  fontWeight: "300",
  fontSize: "12px",
  lineHeight: "24px",
  letterSpacing: "0px",
  verticalAlign: "middle",
  alignSelf: "flex-end",
  color: "rgba(33, 33, 33, 0.8)",
  marginTop: "8px",
});

export const Option = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",

  width: "318px",
  height: "48px",

  borderRadius: "6px",
  border: "1px solid #E0E0E0",

  backgroundColor: "$white",

  padding: "14px 20px 14px 16px",

  fontWeight: "300",
  fontSize: "14px",
  lineHeight: "100%",
  color: "$charcoal",

  svg: {
    marginTop: "2px",
  },

  variants: {
    isSelected: {
      true: {
        borderColor: "$secondary-light",
      },
    },
  },
});

export const OptionsContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  margin: "48px 0 40px 0",
});

export const ResponseContainer = styled("div", {
  position: "relative",

  width: "754px",
  height: "100%",
  minHeight: "62px",

  margin: "0 0 -18px -17px",
  padding: "16px",

  backgroundColor: "$gray-20",
  boxShadow: "0px 0px 2px 0px rgba(0, 0, 0, 0.26)",

  borderBottomLeftRadius: "12px",
  borderBottomRightRadius: "12px",

  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "16px",
});

/**
 * Keeps the response bar inside the card however long the conversation gets. The modal caps at 873px
 * (minus its 16px padding each side): the list gives way and scrolls, while the bar never shrinks —
 * it grows with the textarea exactly as it does on its own.
 */
export const ThreadLayout = styled("div", {
  display: "flex",
  flexDirection: "column",
  maxHeight: "min(841px, calc(100vh - 64px))",

  [`& > ${Container}`]: {
    flex: "1 1 auto",
    minHeight: 0,
    height: "auto",
    maxHeight: "none",
  },

  [`& > ${ResponseContainer}`]: {
    flexShrink: 0,
    height: "auto",
  },
});

export const ArticleCard = styled("a", {
  display: "flex",
  alignItems: "center",
  gap: "20px",
  marginTop: "12px",
  padding: "10px 12px",
  borderRadius: "8px",
  border: "1px solid $gray-30",
  backgroundColor: "$gray-20",
  textDecoration: "none",
  color: "$black",
  cursor: "pointer",

  "&:hover": {
    backgroundColor: "$gray-30",
  },

  variants: {
    disabled: {
      true: {
        cursor: "default",
        pointerEvents: "none",
        opacity: 0.6,
        backgroundColor: "transparent",
        borderStyle: "dashed",
      },
    },
  },
});

export const ArticleCardIcon = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

export const ArticleCardText = styled("div", {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
});

export const ArticleCardLabel = styled("span", {
  fontSize: "11px",
  lineHeight: "14px",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  color: "$gray-90",
});

export const ArticleCardTitle = styled("span", {
  fontFamily: "$openSans",
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: "20px",
  color: "$primary-30",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",

  variants: {
    disabled: {
      true: {
        color: "$gray-90",
        fontStyle: "italic",
        fontWeight: 500,
      },
    },
  },
});

/** The patient's pick on a multiple-choice question, drawn as the option itself. */
export const AnsweredOption = styled("div", {
  padding: "38px 22px 22px 32px",
});

/** Where a just-sent message is about to appear while the conversation reloads. */
export const SendingIndicator = styled("div", {
  alignSelf: "flex-end",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "48px",
  padding: "0 18px",
});

/** The "Close this thread" affordance, and the plain text it becomes once closed. */
export const CloseThreadRow = styled("div", {
  display: "flex",
  justifyContent: "center",
  width: "100%",
  marginTop: "-16px",
  position: "relative",
  top: "12px",
});

export const CloseThreadLink = styled("button", {
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",

  fontFamily: "inherit",
  fontSize: "14px",
  fontWeight: "400",
  lineHeight: "24px",
  color: "$primary-30",
  textDecoration: "underline",

  "&:hover": {
    color: "$primary",
  },

  "&:disabled": {
    cursor: "default",
    textDecoration: "none",
    color: "$gray-90",
  },
});

export const ThreadClosedText = styled("p", {
  fontSize: "14px",
  fontWeight: "400",
  lineHeight: "24px",
  color: "$gray-90",
});

export const PlusButton = styled("button", {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  width: "28px",
  height: "28px",
  flexShrink: 0,

  background: "none",
  border: "none",
  borderRadius: "50%",
  cursor: "pointer",

  fontSize: "24px",
  lineHeight: "24px",
  color: "$charcoal",

  "&:hover": {
    backgroundColor: "$gray-30",
  },

  "&:disabled": {
    cursor: "default",
    color: "$gray-90",
    backgroundColor: "transparent",
  },
});

export const PlusMenu = styled("div", {
  position: "absolute",
  bottom: "62px",
  left: "16px",
  zIndex: 1,

  width: "280px",

  display: "flex",
  flexDirection: "column",

  backgroundColor: "$white",
  borderRadius: "8px",
  boxShadow: "0px 2px 12px 0px rgba(0, 0, 0, 0.16)",
  overflow: "hidden",
});

export const PlusMenuItem = styled("button", {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "2px",

  width: "100%",
  padding: "16px 20px",

  background: "none",
  border: "none",
  borderBottom: "1px solid $gray-30",
  textAlign: "left",
  cursor: "pointer",
  fontFamily: "inherit",

  "&:last-child": {
    borderBottom: "none",
  },

  "&:hover": {
    backgroundColor: "$gray-20",
  },

  "&:disabled": {
    cursor: "default",
    backgroundColor: "transparent",
  },
});

export const PlusMenuTitle = styled("span", {
  fontSize: "14px",
  fontWeight: "600",
  color: "$black",

  variants: {
    disabled: {
      true: { color: "$gray-90" },
    },
  },
});

export const PlusMenuDescription = styled("span", {
  fontSize: "13px",
  fontWeight: "300",
  color: "$gray-90",
});

export const EmptyState = styled("p", {
  padding: "46px 32px",
  color: "$gray-90",
});

/**
 * The modal is opened with `showCloseButton: false`, because the global X calls closeModal() and the
 * ticket wants X on the Custom step to come back to the conversation. So each step renders its own,
 * placed exactly where the Modal's own X sits and wrapping the same CustomIconButton.
 */
export const HeaderClose = styled("button", {
  position: "absolute",
  right: 8,
  top: 8,
  zIndex: "1",

  backgroundColor: "transparent",
  border: "none",
  padding: 0,
});

// CSS

export const TextAreaCss = css({
  div: {
    textArea: {
      scrollbarWidth: "none",
      width: "566px !important",

      minHeight: "48px !important",
      maxHeight: "200px !important",
      height: "48px",

      lineHeight: "24px",

      resize: "vertical",
      padding: "4px 12px",
    },
  },
});

export const SendButtonCss = css({
  button: {
    width: "90px",
    height: "28px",
    borderRadius: "8px",

    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    p: {
      fontSize: "14px",
      fontWeight: "500",
      lineHeight: "22px",
    },
  },
});

/**
 * On a closed thread the wider "Send & reopen" button does not fit beside the fixed 566px textarea, so
 * the textarea takes whatever the bar has left instead. An open thread keeps the fixed width.
 */
export const ReopenTextAreaCss = css({
  flex: "1 1 auto",
  minWidth: 0,

  div: {
    textArea: {
      width: "100% !important",
    },
  },
});

/** Same button, wide enough for "Send & reopen" on a closed thread. */
export const SendAndReopenButtonCss = css({
  button: {
    width: "max-content",
    padding: "0 12px",

    p: {
      whiteSpace: "nowrap",
    },
  },
});
